import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Button } from '@growtherapy/sprout-ui';

import { LanguageSwitcherWrapper } from '../../components/LanguageSwitcherWrapper';
import { NavbarWrapper } from '../../components/NavbarWrapper';
import { PageContainer } from '../../components/PageContainer';
import { PageProviders } from '../../components/PageProviders';
import { PageComponentProps } from '../../components/PaginatedDrawer';
import { MeasuresDrawerLoadingSkeleton } from '../../components/measures/MeasuresDrawer';
import { PendingMeasuresForm } from '../../components/measures/PendingMeasuresForm';
import { MeasureSchemaAndAnswers, PendingMeasuresFormLayout } from '../../components/measures/types';
import { Theme } from '../../state/theme';
import { buildMeasurePages, filterMeasuresByType } from '../measures/helpers';
import { staleSoftBlockMeasureTypesAtom, visitorMeasuresAtom } from '../state';
import { PreSessionMeasuresActions } from './PreSessionMeasuresActions';
import { useSoftBlockMeasuresTracking } from './useSoftBlockTracking';

export function PreSessionMeasures({ onComplete, onSkip }: { onComplete: () => void; onSkip: () => void }) {
  const t = useTranslate();
  const measures = useAtomValue(visitorMeasuresAtom);
  const staleMeasureTypes = useAtomValue(staleSoftBlockMeasureTypesAtom);
  const pages = buildMeasurePages(filterMeasuresByType(measures, staleMeasureTypes));
  const { handlePageIndexChange, trackCurrentMeasureSkipped } = useSoftBlockMeasuresTracking(pages);

  const handleSkip = useCallback(() => {
    trackCurrentMeasureSkipped();
    onSkip();
  }, [trackCurrentMeasureSkipped, onSkip]);

  const actionsComponent = useCallback(
    (props: PageComponentProps<MeasureSchemaAndAnswers>) => (
      <PreSessionMeasuresActions {...props} onAllMeasuresCompleted={onComplete} />
    ),
    [onComplete],
  );

  return (
    <PageProviders theme={Theme.LIGHT}>
      <PageContainer>
        <NavbarWrapper languageSwitcherComponent={LanguageSwitcherWrapper} />

        <PendingMeasuresForm
          layout={PendingMeasuresFormLayout.PAGE}
          pages={pages}
          actionsComponent={actionsComponent}
          onPageIndexChange={handlePageIndexChange}
          loadingFallback={<MeasuresDrawerLoadingSkeleton />}
          emptyFallback={
            <div className='flex min-h-0 w-full flex-1 items-center justify-center'>
              <Button use='primary' onClick={onSkip}>
                {t('telehealth-core:preSessionMeasures.continueToWaitingRoom')}
              </Button>
            </div>
          }
          footerContent={
            <Button use='secondary' size='sm' onClick={handleSkip}>
              {t('telehealth-core:preSessionMeasures.skip')}
            </Button>
          }
        />
      </PageContainer>
    </PageProviders>
  );
}
